import { useCallback, useMemo } from "react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useToast } from "src/hooks/useToast";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";
import { formatDate, formatDateHour, formatDateMinute, formatDateSecond, formatDateYYYYMMDD, validateString } from "src/utils/string/stringUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { postMST7020_R01, deleteMST7020_R01, downloadExcel_MST7020_R01, getMST7020_R01List, patchMST7020_R01, getMST7020_R01AllCount } from './Api_MST7020_R01';
import { defaultMST7020_R01Dto, MST7020_R01Dto } from "./Dto_MST7020_R01";
import { defaultMST7020_R01Filter, MST7020_R01Filter, useMST7020_R01Store } from './Store_MST7020_R01';
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import useUserStore from "src/store/useUserStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";


interface Props {
	gridRef: React.RefObject<AgGridReact<MST7020_R01Dto> | null>;
	setMessage: (message: string) => void;	
  }

const getParams = (filter: MST7020_R01Filter) => {
	const params: MST7020_R01Filter = { 
		...filter
	};
	
	return params;
}
  
export const Content_MST7020_R01 = (props: Props) => {
	const {
		gridRef,
		setMessage,
	} = props;

	const { 
		MST7020_R01List, setMST7020_R01List, 
		MST7020_R01PastFilter, setMST7020_R01PastFilter,
		MST7020_R01Filter, 
		selectedMST7020_R01, setSelectedMST7020_R01,
		editMST7020_R01, setEditMST7020_R01,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useMST7020_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { success, error: showError } = useToast();
	const { employeeCdList } = useCommonCodeStore();
	const { company, userInfo } = useUserStore(); 

	const handleExcelClick = useCallback(async () => {
		
		setLoadingSpinner(true);

		const { isDefaultFilter, isDefaultPastFilter, listLength } = fetchFilterConfig(
			MST7020_R01Filter,
			MST7020_R01PastFilter,
			defaultMST7020_R01Filter,
			MST7020_R01List.length
		);
		
		const response = await downloadExcel_MST7020_R01(
			isDefaultFilter? '' : 'search',
			getParams(MST7020_R01Filter),
			listLength
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '문자메세지 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [MST7020_R01Filter, setMessage, showError]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST7020_R01AllCount(getParams(MST7020_R01Filter));
		setAllCount(allCountResponse.data.allCount);
		return allCountResponse.data.allCount;
	}, [MST7020_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		try {


			const response = await getMST7020_R01List(
				'search',
				getParams(MST7020_R01Filter),
				MST7020_R01List.length ?? 0
			);
			
			const newList = response.data.SearchResult as MST7020_R01Dto[];
			setMST7020_R01List(newList);
			
			// 백엔드 응답 구조에 맞게 데이터 추출

			const processAllCount = await renewalAllCount();
			if(newList.length > 0) {
				gridRefSelectedRowHandler(
					gridRef,
					setSelectedMST7020_R01,
					() => true,// () => !accountData.find(value => value.bankAcctCd === selectedMST7020_R01?.bankAcctCd)
				);
				setMST7020_R01PastFilter(MST7020_R01Filter);
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			} else {
				showWarning('조회된 데이터가 없습니다.', 3000);
			}
		} catch (error) {
			showError("조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [MST7020_R01Filter, MST7020_R01List, gridRef, editMST7020_R01, setMST7020_R01List, setSelectedMST7020_R01, setMST7020_R01PastFilter, showError, setLoadingSpinner, setScrollTop]);

	const defaultMST7020_R01Row = useCallback(() => {
		return {
			...defaultMST7020_R01Dto,
			sendMsgId: 0,
			sendDT: formatDate(new Date()), // 오늘 날짜
			resultDT: formatDate(new Date()), // 기본값으로 오늘 날짜
			inserted: true,
		}
	}, [defaultMST7020_R01Dto]);

	const initContainerData = useCallback(async () => {
		// 전체 초기화: 검색 조건, 테이블, Info 패널 모두 초기화
		initData();
		setMessage("검색 조건 및 테이블이 모두 초기화되었습니다.");
		setTimeout(() => setMessage(""), 5000);
	}, [initData, setMessage]);

	const handleCreateClick = useCallback(async () => {
		setSelectedMST7020_R01(undefined);
		setEditMST7020_R01({ ...defaultMST7020_R01Row() });
	}, [setEditMST7020_R01, setSelectedMST7020_R01]);

	const handleDeleteClick = useCallback(async () => {
			// 삭제 표시된 항목이 없으면 선택된 계좌를 삭제 목록에 추가
		if (!selectedMST7020_R01) {
			showError("삭제할 계좌를 선택해주세요."); return;
		}

		let responseData: MST7020_R01Dto[] = [];

		if (window.confirm('정말로 삭제하시겠습니까?')) {
			
			const response = await deleteMST7020_R01([selectedMST7020_R01]);

			responseData = response.data.SearchResult as MST7020_R01Dto[];

			refetchUpdateList('patch', responseData[0], editMST7020_R01, 'sendMsgId', MST7020_R01List, setMST7020_R01List);
			showSuccess(`${selectedMST7020_R01.sendMsgId} 문자요청이 취소되었습니다.`)
		}
		if (responseData && responseData.length > 0) {
			setSelectedMST7020_R01(responseData[0]);
		}

		
	}, [selectedMST7020_R01, setSelectedMST7020_R01, setEditMST7020_R01, showError, setMessage]);

	const validateInsertData = (data: MST7020_R01Dto) => {

		if (!validateString(data.sendType))									{ showWarning("문자 유형을 선택해주세요.");				return false; }
		if (!validateString(data.msgContent))								{ showWarning("내용을 입력해주세요.");					return false; }
		if (!validateString(data.subject) && data.sendType === 'long')		{ showWarning("장문문자 발송시 제목을 입력해주세요.");	return false; }
		if (data.sendMsgId === 0 && !data.sendTarget.length ){
			showWarning("대상자는 최소 1명 이상 있어야 합니다.");	return false;
		}
		
		
		return true;
	}

	const handleSaveClick = useCallback(async () => {
		// 삭제할 항목도 없고 편집할 데이터도 없으면 에러
		if (!editMST7020_R01) {
			showError("저장할 데이터가 없습니다.");
			return;
		}

		// try {
			let operationMessage = "";
			let processName = '';
			let responseData: MST7020_R01Dto[] = [];

			const isPatch = selectedMST7020_R01 && selectedMST7020_R01.sendMsgId;

			// 2. 등록처리
			if (editMST7020_R01) {
				// 데이터 검증

				if(!validateInsertData(editMST7020_R01)) return;

				if (isPatch) {
					processName = 'patch';
					// 수정 모드 - U_01 API 사용
					const response = await patchMST7020_R01([editMST7020_R01]);
					responseData = response.data.SearchResult as MST7020_R01Dto[];
					operationMessage += "문자 요청이 수정되었습니다.";
				} else {
					// 등록 모드 - C_01 API 사용
					processName = 'post';

					const requestMST7020_R01List : MST7020_R01Dto[] = [];

					const senderEmp = employeeCdList.find(emp => emp.empNo === userInfo?.empNo);

					const sendType = editMST7020_R01.sendType as 'short' | 'long';
					const reserveType = editMST7020_R01.reserveDT ? 'reserve' : 'direct';
					const reserveDTString = editMST7020_R01.reserveDT ? formatDateYYYYMMDD(editMST7020_R01.reserveDT) + ':' + formatDateHour(editMST7020_R01.reserveDT) + formatDateMinute(editMST7020_R01.reserveDT) + formatDateSecond(editMST7020_R01.reserveDT) : '';

					for(let i = 0; i < editMST7020_R01.sendTarget.length; i++) {
						requestMST7020_R01List.push({
							...editMST7020_R01,
							receiver: editMST7020_R01.sendTarget[i].mobileNo.replace(/-/g, ''),
							receiverName: editMST7020_R01.sendTarget[i].empNm.replace(' (부서장)', ''),
							sender: senderEmp?.mobileNo.replace(/-/g, '') ?? '',
							senderName: senderEmp?.empNm ?? '',
							reserveDT: reserveDTString,
						});
					}

					const response = await postMST7020_R01(requestMST7020_R01List, sendType, reserveType);
					responseData = response.data.SearchResult as MST7020_R01Dto[];
					operationMessage += "문자 요청이 등록되었습니다.";
					if(!response.data.result) {
						showError(response.data.message);
						setMessage(response.data.message);
						setTimeout(() => setMessage(""), 5000);
						return;
					}
				}
			}

			
			showSuccess(operationMessage || "저장이 완료되었습니다.");
			setMessage(operationMessage || "저장이 완료되었습니다.");
			setTimeout(() => setMessage(""), 5000);
			if(editMST7020_R01) {
				if (processName === 'post') {
					const processResponseData = responseData.sort((a, b) => b.sendMsgId - a.sendMsgId);
					const newList = [...processResponseData, ...MST7020_R01List];
					setMST7020_R01List(newList);
				} else {
					// 수정은 단일 항목을 업데이트하므로 기존 로직을 유지할 수 있습니다.
					for(let i = 0; i < responseData.length; i++) {
						refetchUpdateList(processName, responseData[i], editMST7020_R01, 'sendMsgId', MST7020_R01List, setMST7020_R01List);
					}
				}
			}
			if (responseData && responseData.length > 0) {
				setSelectedMST7020_R01(responseData[0]);
			}


			// await fetchData();
		// } catch (error) {
		//     console.error("저장 중 오류 발생:", error);
		//     showError("저장 중 오류가 발생했습니다. : " + error);
		// } finally {
		//     setLoadingSpinner(false);
		// }
	}, [editMST7020_R01, selectedMST7020_R01, setLoadingSpinner, showError, setMessage, initData, fetchData, MST7020_R01List, setMST7020_R01List]);

	useFetchActionbarAuth("MST7020_R01");

	return {
		handleExcelClick,
		fetchData,
		defaultMST7020_R01Row,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	}
}